import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  Skeleton,
  Alert,
  Collapse,
  IconButton,
  Drawer,
  Divider,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  PendingActions,
  Print as PrintIcon,
  CheckCircle as ClearedIcon,
  Warning as UnclearedIcon,
  AccountBalance as BankIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { PieChart } from '@mui/x-charts/PieChart';
import { styled } from '@mui/material/styles';
import { useEasyCheck } from '../../context/EasyCheckContext';
import { formatCurrencyAmount } from '../../utils/currencyUtils';
import { useCheques } from '../../hooks/useCheques';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useNavigate } from 'react-router-dom';
import { pdcAlertService } from '../../services/pdcAlerts/pdcAlertService';

// ─── Styled Components ───────────────────────────────────

const DashboardContainer = styled(Box)({
  height: '100%',
  backgroundColor: '#f1f5f9',
  padding: '24px',
  overflowY: 'auto',
});

const KpiCard = styled(Card)(({ bordercolor }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  borderTop: `3px solid ${bordercolor || '#2563eb'}`,
  boxShadow: 'none',
  transition: 'box-shadow 0.2s, transform 0.2s',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    transform: 'translateY(-2px)',
  },
}));

const SectionCard = styled(Paper)({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  boxShadow: 'none',
  padding: '24px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const BankItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '14px',
  backgroundColor: '#f8fafc',
  borderRadius: '10px',
  border: '1px solid #f1f5f9',
});

const BankIconBox = styled(Box)(({ bgColor }) => ({
  width: 40,
  height: 40,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.85rem',
  fontWeight: 700,
  color: 'white',
  background: bgColor || 'linear-gradient(135deg, #2563eb, #1d4ed8)',
  flexShrink: 0,
}));

const TableRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 0',
  borderBottom: '1px solid #f8fafc',
  '&:last-child': { borderBottom: 'none' },
  '&:hover': { backgroundColor: '#fafbfc' },
});

// ─── Helpers ─────────────────────────────────────────────

const STATUS_COLORS = {
  Pending: { bg: '#fef3c7', color: '#d97706', chart: '#f59e0b' },
  Printed: { bg: '#ede9fe', color: '#7c3aed', chart: '#8b5cf6' },
  Signed: { bg: '#ede7f6', color: '#5e35b1', chart: '#7e57c2' },
  Cleared: { bg: '#d1fae5', color: '#059669', chart: '#10b981' },
  Cancelled: { bg: '#fee2e2', color: '#dc2626', chart: '#ef4444' },
  Bounced: { bg: '#fce7f3', color: '#be185d', chart: '#ec4899' },
};

const BANK_COLORS = [
  'linear-gradient(135deg, #2563eb, #1d4ed8)',
  'linear-gradient(135deg, #059669, #047857)',
  'linear-gradient(135deg, #7c3aed, #6d28d9)',
  'linear-gradient(135deg, #d97706, #b45309)',
  'linear-gradient(135deg, #dc2626, #b91c1c)',
  'linear-gradient(135deg, #0891b2, #0e7490)',
];

const formatAmount = (amount, currency = 'AED') => formatCurrencyAmount(amount, currency);

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
  } catch { return ''; }
};

const getDaysLabel = (daysFromNow) => {
  if (daysFromNow === undefined || daysFromNow === null) return null;
  const days = Number(daysFromNow);
  if (days <= 0) return { label: 'Today', color: 'urgent' };
  if (days === 1) return { label: 'Tomorrow', color: 'urgent' };
  if (days <= 7) return { label: `${days} days`, color: 'soon' };
  return { label: `${days} days`, color: 'normal' };
};

const getBankInitials = (name) => {
  if (!name) return '??';
  const words = name.split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

// ─── Component ───────────────────────────────────────────

const DashboardPage = () => {
  const { user } = useEasyCheck();
  const { showSuccess, showError } = useSnackbar();
  const { getDashboardStatistics, getUpcomingPayments, getCheques, getBankSummary } = useCheques(showSuccess, showError);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [recentCheques, setRecentCheques] = useState([]);
  const [bankSummary, setBankSummary] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('all');
  // Currency for aggregate displays: the selected bank's currency, or AED when "all" banks.
  const selectedCurrency = selectedBankId === 'all'
    ? 'AED'
    : (bankSummary.find((b) => String(b.BankId || b.bankId) === String(selectedBankId))?.Currency
        || bankSummary.find((b) => String(b.BankId || b.bankId) === String(selectedBankId))?.currency
        || 'AED');

  // PDC Alerts — loaded async, does not block dashboard
  const [pdcAlerts, setPdcAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pdcAlertsDismissed');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [drawerAlert, setDrawerAlert] = useState(null);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const bankIdParam = selectedBankId !== 'all' ? Number(selectedBankId) : undefined;
      const bankParams = bankIdParam ? { bankId: bankIdParam } : {};

      const [statsRes, upcomingRes, recentRes, bankRes] = await Promise.all([
        getDashboardStatistics(bankParams),
        getUpcomingPayments({ pageSize: 5, ...bankParams }),
        getCheques({ pageSize: 5, ...(bankIdParam ? { bankId: bankIdParam } : {}) }),
        getBankSummary(),
      ]);

      if (statsRes) {
        setStats({
          totalCheques: (statsRes.StatusBreakdown?.Pending || statsRes.StatusBreakdown?.pending || 0)
            + (statsRes.StatusBreakdown?.Printed || statsRes.StatusBreakdown?.printed || 0)
            + (statsRes.StatusBreakdown?.Cleared || statsRes.StatusBreakdown?.cleared || 0)
            + (statsRes.StatusBreakdown?.Cancelled || statsRes.StatusBreakdown?.cancelled || 0)
            + (statsRes.StatusBreakdown?.Bounced || statsRes.StatusBreakdown?.bounced || 0),
          totalValue: statsRes.TotalValue || statsRes.totalValue || 0,
          pending: statsRes.StatusBreakdown?.Pending || statsRes.StatusBreakdown?.pending || 0,
          printed: statsRes.StatusBreakdown?.Printed || statsRes.StatusBreakdown?.printed || 0,
          cleared: statsRes.StatusBreakdown?.Cleared || statsRes.StatusBreakdown?.cleared || 0,
          cancelled: statsRes.StatusBreakdown?.Cancelled || statsRes.StatusBreakdown?.cancelled || 0,
          bounced: statsRes.StatusBreakdown?.Bounced || statsRes.StatusBreakdown?.bounced || 0,
          periodSummary: statsRes.PeriodSummary || statsRes.periodSummary || null,
        });
      }

      if (upcomingRes?.success) {
        setUpcoming(Array.isArray(upcomingRes.data) ? upcomingRes.data.slice(0, 5) : []);
      } else {
        setUpcoming([]);
      }

      if (recentRes?.success) {
        setRecentCheques(Array.isArray(recentRes.data) ? recentRes.data.slice(0, 5) : []);
      }

      if (Array.isArray(bankRes)) {
        setBankSummary(bankRes);
      }
    } catch {
      showError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [getDashboardStatistics, getUpcomingPayments, getCheques, getBankSummary, showError, selectedBankId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // PDC Alerts — reload when bank filter changes
  useEffect(() => {
    const checkPdcAlerts = async () => {
      try {
        const bankIdParam = selectedBankId !== 'all' ? Number(selectedBankId) : undefined;
        const result = await pdcAlertService.checkAlerts(bankIdParam ? { bankId: bankIdParam } : {});
        const data = result?.data || {};
        const hasAlerts = data.HasAlerts ?? data.hasAlerts ?? false;
        const alerts = data.Alerts ?? data.alerts ?? [];
        setPdcAlerts(hasAlerts && alerts.length > 0 ? alerts : []);
      } catch {
        // Silent fail — alerts are non-critical
      }
    };
    checkPdcAlerts();
  }, [selectedBankId]);

  const handleDismissAlert = (alertDays) => {
    const updated = [...dismissedAlerts, alertDays];
    setDismissedAlerts(updated);
    sessionStorage.setItem('pdcAlertsDismissed', JSON.stringify(updated));
  };

  const visibleAlerts = pdcAlerts.filter((a) => {
    const days = a.AlertDays ?? a.alertDays;
    return !dismissedAlerts.includes(days);
  });

  // Derived data
  const unclearedCount = (stats?.pending || 0) + (stats?.printed || 0);
  const unclearedValue = 0; // Will be calculated from bank summary or stats

  const pendingAmount = bankSummary.reduce((sum, b) => sum + (b.PendingCount || 0), 0);
  const printedAmount = bankSummary.reduce((sum, b) => sum + (b.PrintedCount || 0), 0);

  const pieData = stats ? [
    { id: 'Pending', value: stats.pending, label: 'Pending', color: STATUS_COLORS.Pending.chart },
    { id: 'Printed', value: stats.printed, label: 'Printed', color: STATUS_COLORS.Printed.chart },
    { id: 'Cleared', value: stats.cleared, label: 'Cleared', color: STATUS_COLORS.Cleared.chart },
    { id: 'Cancelled', value: stats.cancelled, label: 'Cancelled', color: STATUS_COLORS.Cancelled.chart },
    { id: 'Bounced', value: stats.bounced, label: 'Bounced', color: STATUS_COLORS.Bounced.chart },
  ].filter(d => d.value > 0) : [];

  const periodSummary = stats?.periodSummary;
  const thisMonth = periodSummary?.ThisMonth || periodSummary?.thisMonth;
  const lastMonth = periodSummary?.LastMonth || periodSummary?.lastMonth;
  const pctChange = periodSummary?.PercentageChange ?? periodSummary?.percentageChange ?? null;

  // KPI Cards config
  const kpiCards = [
    {
      title: 'TOTAL CHEQUES',
      value: stats?.totalCheques || 0,
      subtitle: formatAmount(stats?.totalValue),
      trend: pctChange !== null ? `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(1)}% vs last month` : null,
      trendUp: pctChange >= 0,
      color: '#2563eb',
      icon: <ReceiptIcon sx={{ fontSize: 20 }} />,
    },
    {
      title: 'PENDING',
      value: stats?.pending || 0,
      subtitle: `${stats?.pending || 0} awaiting print`,
      color: '#f59e0b',
      icon: <PendingActions sx={{ fontSize: 20 }} />,
    },
    {
      title: 'PRINTED',
      value: stats?.printed || 0,
      subtitle: 'Printed, not yet cleared',
      color: '#8b5cf6',
      icon: <PrintIcon sx={{ fontSize: 20 }} />,
    },
    {
      title: 'CLEARED',
      value: stats?.cleared || 0,
      subtitle: 'Successfully cleared',
      color: '#10b981',
      icon: <ClearedIcon sx={{ fontSize: 20 }} />,
    },
    {
      title: 'UNCLEARED',
      value: unclearedCount,
      subtitle: 'Pending + Printed',
      color: '#ef4444',
      icon: <UnclearedIcon sx={{ fontSize: 20 }} />,
    },
  ];

  if (loading) {
    return (
      <DashboardContainer>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={300} height={36} />
          <Skeleton variant="text" width={200} height={20} />
        </Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1,2,3,4,5].map(i => (
            <Grid item xs={12} sm={6} md={2.4} key={i}>
              <Skeleton variant="rounded" height={130} sx={{ borderRadius: '12px' }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={7}><Skeleton variant="rounded" height={300} sx={{ borderRadius: '12px' }} /></Grid>
          <Grid item xs={12} md={5}><Skeleton variant="rounded" height={300} sx={{ borderRadius: '12px' }} /></Grid>
        </Grid>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Cash Management Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Welcome back, {user?.name || 'User'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={selectedBankId}
              onChange={(e) => setSelectedBankId(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: 600,
                '& .MuiSelect-select': { py: 1 },
              }}
              startAdornment={
                <BankIcon sx={{ fontSize: 18, color: '#64748b', mr: 1 }} />
              }
            >
              <MenuItem value="all">All Bank Accounts</MenuItem>
              {bankSummary.map((bank) => {
                const bankId = bank.BankId || bank.bankId;
                const name = bank.AccountName || bank.accountName || '';
                const currency = bank.Currency || bank.currency || '';
                return (
                  <MenuItem key={bankId} value={bankId}>
                    {name} ({currency})
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Chip
            label={new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            sx={{ backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 600, fontSize: '0.8rem' }}
          />
        </Box>
      </Box>

      {/* PDC Alert Banners */}
      {visibleAlerts.map((alert) => {
        const days = alert.AlertDays ?? alert.alertDays;
        const count = alert.ChequeCount ?? alert.chequeCount ?? 0;
        const total = alert.TotalAmount ?? alert.totalAmount ?? 0;
        const severity = days <= 7 ? 'error' : days <= 14 ? 'warning' : 'info';
        const formattedTotal = formatCurrencyAmount(total, selectedCurrency, { withSymbol: false });
        return (
          <Collapse in key={days}>
            <Alert
              severity={severity}
              sx={{ mb: 2, alignItems: 'center' }}
              action={
                <IconButton size="small" color="inherit" onClick={() => handleDismissAlert(days)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {count} cheque{count !== 1 ? 's' : ''} due within {days} days — Total: {formattedTotal}
              </Typography>
              <Typography
                variant="caption"
                sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'inherit', mt: 0.5, display: 'inline-block' }}
                onClick={() => setDrawerAlert(alert)}
              >
                Show Details
              </Typography>
            </Alert>
          </Collapse>
        );
      })}

      {/* Row 1: KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card, i) => (
          <Grid item xs={12} sm={6} md={2.4} key={i} sx={{ display: 'flex' }}>
            <KpiCard bordercolor={card.color} sx={{ flex: 1, display: 'flex' }}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', color: '#64748b' }}>
                    {card.title}
                  </Typography>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                </Box>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', lineHeight: 1, mb: 0.5 }}>
                  {card.value.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#64748b', flex: 1 }}>
                  {card.subtitle}
                </Typography>
                {card.trend && (
                  <Chip
                    label={card.trend}
                    size="small"
                    sx={{
                      mt: 1,
                      height: 22,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      backgroundColor: card.trendUp ? '#ecfdf5' : '#fef2f2',
                      color: card.trendUp ? '#059669' : '#dc2626',
                    }}
                  />
                )}
              </CardContent>
            </KpiCard>
          </Grid>
        ))}
      </Grid>

      {/* Row 2: Upcoming Payments + Bank Summary */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Upcoming Payments */}
        <Grid item xs={12} md={7}>
          <SectionCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>Upcoming Payments</Typography>
              <Typography
                sx={{ fontSize: '0.8rem', color: '#2563eb', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => navigate('/print-cheque')}
              >
                View All →
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', mb: 2 }}>Cheques due in the next 30 days</Typography>

            {/* Table Header */}
            <Box sx={{ display: 'flex', px: 1, pb: 1, borderBottom: '2px solid #f1f5f9' }}>
              <Typography sx={{ flex: 2, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Beneficiary</Typography>
              <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'right' }}>Amount</Typography>
              <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'center' }}>Due Date</Typography>
              <Typography sx={{ flex: 0.7, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'center' }}>Due In</Typography>
            </Box>

            {upcoming.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>No upcoming payments</Typography>
              </Box>
            ) : (
              upcoming.map((row, i) => {
                const name = row.BeneficiaryName || row.beneficiaryName || '';
                const amount = row.ChqAmount || row.chqAmount || 0;
                const currency = row.Currency || row.currency || 'AED';
                const date = row.ChqDate || row.chqDate || '';
                const daysFromNow = row.DaysFromNow ?? row.daysFromNow;
                const dayInfo = getDaysLabel(daysFromNow);

                return (
                  <TableRow key={i} sx={{ px: 1 }}>
                    <Typography sx={{ flex: 2, fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>{name}</Typography>
                    <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', fontFamily: 'monospace', textAlign: 'right' }}>
                      {formatAmount(amount, currency)}
                    </Typography>
                    <Typography sx={{ flex: 1, fontSize: '0.82rem', color: '#64748b', textAlign: 'center' }}>{formatDate(date)}</Typography>
                    <Box sx={{ flex: 0.7, textAlign: 'center' }}>
                      {dayInfo && (
                        <Chip
                          label={dayInfo.label}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            backgroundColor: dayInfo.color === 'urgent' ? '#fef2f2' : dayInfo.color === 'soon' ? '#fef3c7' : '#f0f9ff',
                            color: dayInfo.color === 'urgent' ? '#dc2626' : dayInfo.color === 'soon' ? '#d97706' : '#2563eb',
                          }}
                        />
                      )}
                    </Box>
                  </TableRow>
                );
              })
            )}
          </SectionCard>
        </Grid>

        {/* Bank Account Summary */}
        <Grid item xs={12} md={5}>
          <SectionCard>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', mb: 0.5 }}>Bank Account Summary</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', mb: 2 }}>Cheque distribution by bank</Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
              {(() => {
                const filteredBanks = selectedBankId === 'all'
                  ? bankSummary
                  : bankSummary.filter(b => String(b.BankId || b.bankId) === String(selectedBankId));
                return filteredBanks.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>No bank accounts configured</Typography>
                </Box>
              ) : (
                filteredBanks.map((bank, i) => {
                  const name = bank.AccountName || bank.accountName || '';
                  const acct = bank.AccountNumber || bank.accountNumber || '';
                  const currency = bank.Currency || bank.currency || 'AED';
                  const total = bank.TotalAmount || bank.totalAmount || 0;
                  const count = bank.ChequeCount || bank.chequeCount || 0;

                  return (
                    <BankItem key={i}>
                      <BankIconBox bgColor={BANK_COLORS[i % BANK_COLORS.length]}>
                        {getBankInitials(name)}
                      </BankIconBox>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }} noWrap>{name}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace' }}>{acct} ({currency})</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>
                          {formatAmount(total, currency)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>{count} cheques</Typography>
                      </Box>
                    </BankItem>
                  );
                })
              );
              })()}
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Row 3: Status Chart + Recent Activity */}
      <Grid container spacing={2.5}>
        {/* Cheque Status Distribution */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', mb: 0.5 }}>Cheque Status Distribution</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', mb: 2 }}>Breakdown across all bank accounts</Typography>

            {pieData.length > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Box sx={{ position: 'relative', width: 180, height: 180, flexShrink: 0 }}>
                  <PieChart
                    series={[{
                      data: pieData,
                      innerRadius: 50,
                      outerRadius: 85,
                      paddingAngle: 2,
                      cornerRadius: 3,
                    }]}
                    width={180}
                    height={180}
                    slotProps={{ legend: { hidden: true } }}
                  />
                  <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{stats?.totalCheques || 0}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>Total</Typography>
                  </Box>
                </Box>

                <Box sx={{ flex: 1 }}>
                  {pieData.map((item) => (
                    <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: item.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.85rem', color: '#475569', fontWeight: 500 }}>{item.label}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>{item.value}</Typography>
                        <Typography component="span" sx={{ fontSize: '0.7rem', color: '#94a3b8', ml: 0.5 }}>
                          ({((item.value / (stats?.totalCheques || 1)) * 100).toFixed(1)}%)
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>No cheque data yet</Typography>
              </Box>
            )}

            {/* Month-over-Month */}
            {periodSummary && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2.5, p: 2, backgroundColor: '#f8fafc', borderRadius: '10px' }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>Month-over-Month</Typography>
                <Box sx={{ display: 'flex', gap: 3, ml: 'auto' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase' }}>This Month</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>{thisMonth?.Count ?? thisMonth?.count ?? 0}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase' }}>Last Month</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>{lastMonth?.Count ?? lastMonth?.count ?? 0}</Typography>
                  </Box>
                  {pctChange !== null && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase' }}>Change</Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: pctChange >= 0 ? '#059669' : '#dc2626' }}>
                        {pctChange >= 0 ? '+' : ''}{pctChange.toFixed(1)}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </SectionCard>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>Recent Activity</Typography>
              <Typography
                sx={{ fontSize: '0.8rem', color: '#2563eb', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => navigate('/print-cheque')}
              >
                View All →
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', mb: 2 }}>Latest cheque transactions</Typography>

            {/* Table Header */}
            <Box sx={{ display: 'flex', px: 1, pb: 1, borderBottom: '2px solid #f1f5f9' }}>
              <Typography sx={{ flex: 0.6, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Cheque #</Typography>
              <Typography sx={{ flex: 1.5, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Beneficiary</Typography>
              <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'right' }}>Amount</Typography>
              <Typography sx={{ flex: 0.6, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'center' }}>Status</Typography>
            </Box>

            {recentCheques.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>No recent activity</Typography>
              </Box>
            ) : (
              recentCheques.map((cheque, i) => {
                const chequeNum = cheque.ChequeNumber || cheque.chequeNumber || '';
                const name = cheque.BeneficiaryName || cheque.beneficiaryName || '';
                const amount = cheque.ChqAmount || cheque.chqAmount || 0;
                const currency = cheque.Currency || cheque.currency || 'AED';
                const status = cheque.Status || cheque.status || 'Pending';
                const statusStyle = STATUS_COLORS[status] || STATUS_COLORS.Pending;

                return (
                  <TableRow key={i} sx={{ px: 1 }}>
                    <Typography sx={{ flex: 0.6, fontSize: '0.85rem', fontWeight: 600, fontFamily: 'monospace', color: '#0f172a' }}>{chequeNum}</Typography>
                    <Typography sx={{ flex: 1.5, fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }} noWrap>{name}</Typography>
                    <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 700, fontFamily: 'monospace', color: '#0f172a', textAlign: 'right' }}>
                      {formatAmount(amount, currency)}
                    </Typography>
                    <Box sx={{ flex: 0.6, textAlign: 'center' }}>
                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color,
                        }}
                      />
                    </Box>
                  </TableRow>
                );
              })
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* PDC Alert Details Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(drawerAlert)}
        onClose={() => setDrawerAlert(null)}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.3)' } },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            backgroundColor: 'rgba(255, 255, 255, 0.97)',
            backdropFilter: 'blur(8px)',
          },
        }}
      >
        {drawerAlert && (() => {
          const days = drawerAlert.AlertDays ?? drawerAlert.alertDays;
          const count = drawerAlert.ChequeCount ?? drawerAlert.chequeCount ?? 0;
          const total = drawerAlert.TotalAmount ?? drawerAlert.totalAmount ?? 0;
          const cheques = drawerAlert.Cheques ?? drawerAlert.cheques ?? [];
          const severity = days <= 7 ? 'error' : days <= 14 ? 'warning' : 'info';
          const severityColor = severity === 'error' ? '#dc2626' : severity === 'warning' ? '#d97706' : '#2563eb';
          const sorted = [...cheques].sort((a, b) => (a.DaysFromNow ?? a.daysFromNow ?? 0) - (b.DaysFromNow ?? b.daysFromNow ?? 0));

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Header */}
              <Box sx={{ p: 2.5, backgroundColor: severity === 'error' ? '#fef2f2' : severity === 'warning' ? '#fffbeb' : '#eff6ff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                      {count} cheque{count !== 1 ? 's' : ''} due within {days} days
                    </Typography>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: severityColor, mt: 0.5 }}>
                      Total: {formatCurrencyAmount(total, selectedCurrency, { withSymbol: false })}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setDrawerAlert(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider />

              {/* Cheque List */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {/* Table Header */}
                <Box sx={{ display: 'flex', px: 1, pb: 1, borderBottom: '2px solid #f1f5f9' }}>
                  <Typography sx={{ flex: 0.8, fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Chq #</Typography>
                  <Typography sx={{ flex: 1.5, fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Beneficiary</Typography>
                  <Typography sx={{ flex: 1, fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Bank</Typography>
                  <Typography sx={{ flex: 0.8, fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'right' }}>Amount</Typography>
                  <Typography sx={{ flex: 0.7, fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'center' }}>Due Date</Typography>
                  <Typography sx={{ flex: 0.5, fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', textAlign: 'center' }}>Days</Typography>
                </Box>

                {sorted.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>No cheque details available</Typography>
                  </Box>
                ) : (
                  sorted.map((cheque, i) => {
                    const chqNum = cheque.ChequeNumber ?? cheque.chequeNumber ?? '';
                    const beneficiary = cheque.BeneficiaryName ?? cheque.beneficiaryName ?? '';
                    const bank = cheque.BankName ?? cheque.bankName ?? '';
                    const amount = cheque.ChqAmount ?? cheque.chqAmount ?? 0;
                    const currency = cheque.Currency ?? cheque.currency ?? 'AED';
                    const date = cheque.ChqDate ?? cheque.chqDate ?? '';
                    const daysLeft = cheque.DaysFromNow ?? cheque.daysFromNow ?? 0;
                    const dayInfo = getDaysLabel(daysLeft);

                    return (
                      <Box
                        key={cheque.Id ?? cheque.id ?? i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 1.2,
                          px: 1,
                          borderBottom: '1px solid #f8fafc',
                          '&:hover': { backgroundColor: '#fafbfc' },
                        }}
                      >
                        <Typography sx={{ flex: 0.8, fontSize: '0.82rem', fontWeight: 600, fontFamily: 'monospace', color: '#0f172a' }} noWrap>{chqNum}</Typography>
                        <Typography sx={{ flex: 1.5, fontSize: '0.82rem', fontWeight: 500, color: '#0f172a' }} noWrap>{beneficiary}</Typography>
                        <Typography sx={{ flex: 1, fontSize: '0.78rem', color: '#64748b' }} noWrap>{bank}</Typography>
                        <Typography sx={{ flex: 0.8, fontSize: '0.82rem', fontWeight: 700, fontFamily: 'monospace', color: '#0f172a', textAlign: 'right' }}>
                          {formatAmount(amount, currency)}
                        </Typography>
                        <Typography sx={{ flex: 0.7, fontSize: '0.78rem', color: '#64748b', textAlign: 'center' }}>{formatDate(date)}</Typography>
                        <Box sx={{ flex: 0.5, textAlign: 'center' }}>
                          {dayInfo && (
                            <Chip
                              label={dayInfo.label}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                backgroundColor: dayInfo.color === 'urgent' ? '#fef2f2' : dayInfo.color === 'soon' ? '#fef3c7' : '#f0f9ff',
                                color: dayInfo.color === 'urgent' ? '#dc2626' : dayInfo.color === 'soon' ? '#d97706' : '#2563eb',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* Footer */}
              <Divider />
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography
                  sx={{ fontSize: '0.85rem', color: '#2563eb', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => { setDrawerAlert(null); navigate('/pdc-calendar'); }}
                >
                  View in PDC Calendar →
                </Typography>
              </Box>
            </Box>
          );
        })()}
      </Drawer>
    </DashboardContainer>
  );
};

export default DashboardPage;
